import assertNever from 'assert-never';
import { Factory } from 'fishery';
import { DateTime } from 'ts-luxon';

import { DeviceModel, DeviceStatus } from '@shure/shared/angular/data-access/system-api/models';

import { FirmwareUpdateJobRequest, ScheduledJob, ScheduledJobStatus, ScheduledJobType } from '../scheduled-jobs';

interface FirmwareUpdateTaskOptions {
	nodeId?: string;
	hosted?: boolean;
	packageKey?: string;
	packageVersion?: string;
	roomName?: string;
}

function buildFirmwareUpdateTask(
	sequence: number,
	options: FirmwareUpdateTaskOptions = {}
): FirmwareUpdateJobRequest['task'] {
	const { nodeId, hosted = false, packageKey, packageVersion = '1.0.0', roomName } = options;

	return {
		hosted,
		packageVersion,
		packageKey: packageKey ?? `package-key-${sequence}`,
		device: {
			known: true,
			nodeId,
			model: DeviceModel.MXA910,
			hardwareId: `device-hardware-${sequence}`,
			status: DeviceStatus.Online,
			name: `Device ${sequence}`,
			roomName: roomName ?? `Room ${sequence}`,
			currentFirmwareVersion: '0.0.1'
		}
	};
}

export const scheduledJobFactory = Factory.define<ScheduledJob>(({ sequence }) => ({
	id: `scheduled-job-${sequence}`,
	jobId: sequence.toString(),
	name: `Scheduled Job ${sequence}`,
	type: ScheduledJobType.FirmwareUpdate,
	status: ScheduledJobStatus.Pending,
	requestCount: 5,
	executeAt: DateTime.now().plus({ days: 10 }).set({ minute: 0, second: 0, millisecond: 0 }).toJSDate(),
	createdAt: DateTime.now().minus({ days: 10 }).toJSDate(),
	createdBy: `User ${sequence}`,
	updatedAt: DateTime.now().minus({ days: 1 }).toJSDate(),
	updatedBy: `User ${sequence}`
}));

class ScheduledJobRequestFactory extends Factory<FirmwareUpdateJobRequest> {
	public firmwareUpdate(options: FirmwareUpdateTaskOptions = {}): this {
		return this.params({
			type: ScheduledJobType.FirmwareUpdate,
			task: buildFirmwareUpdateTask(this.sequence(), options)
		});
	}

	public withStatus(status: ScheduledJobStatus): this {
		let executeAt = '';
		switch (status) {
			case ScheduledJobStatus.Cancelled:
			case ScheduledJobStatus.Completed:
			case ScheduledJobStatus.Failed:
			case ScheduledJobStatus.Missed:
				executeAt = DateTime.now().minus({ days: 10 }).toJSDate().toISOString();
				break;
			case ScheduledJobStatus.Executing:
				executeAt = DateTime.now().minus({ minutes: 1 }).toJSDate().toISOString();
				break;
			case ScheduledJobStatus.Pending:
				executeAt = DateTime.now().plus({ days: 10 }).toJSDate().toISOString();
				break;
			default:
				assertNever(status);
		}

		return this.params({
			status,
			executeAt
		});
	}
}

export const scheduledJobRequestFactory = ScheduledJobRequestFactory.define(
	({ sequence }): FirmwareUpdateJobRequest => ({
		id: sequence.toString(),
		type: ScheduledJobType.FirmwareUpdate,
		status: ScheduledJobStatus.Pending,
		jobId: `job-${sequence}`,
		executeAt: DateTime.now().plus({ days: 10 }).toJSDate().toISOString(), // scheduled for 10 days from now
		task: buildFirmwareUpdateTask(sequence)
	})
);
